import React, { FC, useCallback, useMemo } from "react";
import {
    SettingsCard,
    SettingsCardAction,
    SettingsCardStatus,
} from "Shared/components/cards";
import { useTranslation } from "Shared/util/custom-hooks";
import { FinaComputedConsentStatus } from "Shared/util/types/api";
import { joinClassNames as c } from "Shared/util/styles";
import { isCriticalConsentStatus } from "../../../../utils";
import styles from "./card.css";
import { useGetConsentStateText } from "./hooks";
import { CardProps } from "./types";

export const Card: FC<CardProps> = ({
    affiliatedBankId,
    bankName,
    bic,
    consents,
    hasPendingConsents,
    onClick,
}) => {
    const t = useTranslation(
        (x) => x.company_settings.payoutInformationPage.consentCards.card,
    );
    const getConsentStateText = useGetConsentStateText();

    const handleClicked = useCallback(
        () => onClick(bic, affiliatedBankId),
        [affiliatedBankId, bic, onClick],
    );

    const settingsCardAction = useMemo<SettingsCardAction>(() => {
        const needsCreation =
            !consents?.length ||
            consents.some(
                (x) => x.status === FinaComputedConsentStatus.NotProvided,
            );

        return {
            loading: hasPendingConsents,
            onClick: handleClicked,
            svg: needsCreation ? "/add.svg" : "/update.svg",
            text: needsCreation
                ? hasPendingConsents
                    ? t.creating
                    : t.create
                : hasPendingConsents
                ? t.renewing
                : t.renew,
        };
    }, [consents, handleClicked, hasPendingConsents, t]);

    const settingsCardStatus = useMemo<SettingsCardStatus>(
        () =>
            consents.some((x) => isCriticalConsentStatus(x.status))
                ? "SEVERE"
                : consents.some(
                      (x) => x.status === FinaComputedConsentStatus.ExpiresSoon,
                  )
                ? "INVALID"
                : "VALID",
        [consents],
    );

    return (
        <SettingsCard
            action={settingsCardAction}
            content={
                <div className={styles.container}>
                    {consents.map(
                        ({
                            accountNumber,
                            customerPaymentMethodId,
                            expiresAt,
                            openBankingAccountId,
                            status,
                        }) => (
                            <div
                                className={c(
                                    styles.grid,
                                    isCriticalConsentStatus(status) &&
                                        styles.redText,
                                )}
                                key={`${customerPaymentMethodId}::${openBankingAccountId}`}
                            >
                                <div className={styles.gridItem}>
                                    {accountNumber}
                                </div>
                                <div className={styles.gridItem}>
                                    {getConsentStateText(status, expiresAt)}
                                </div>
                            </div>
                        ),
                    )}
                </div>
            }
            header={{ text: bankName }}
            status={settingsCardStatus}
        />
    );
};
